import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Plus, 
  Search, 
  Edit2, 
  Trash2, 
  QrCode, 
  ExternalLink,
  Download,
  MoreVertical,
  Eye,
  EyeOff
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { employeeAPI } from '@/lib/api';

const EmployeeList = () => {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, employee: null });
  const [qrDialog, setQrDialog] = useState({ open: false, employee: null, qrData: null });

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await employeeAPI.list();
      setEmployees(response.data);
    } catch (error) {
      toast.error('Failed to load employees');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.employee) return;
    
    try {
      await employeeAPI.delete(deleteDialog.employee.id);
      setEmployees(employees.filter(e => e.id !== deleteDialog.employee.id));
      toast.success('Employee deleted successfully');
    } catch (error) {
      toast.error('Failed to delete employee');
    } finally {
      setDeleteDialog({ open: false, employee: null });
    }
  };

  const handleToggleActive = async (employee) => {
    try {
      await employeeAPI.update(employee.id, { is_active: !employee.is_active });
      setEmployees(employees.map(e => 
        e.id === employee.id ? { ...e, is_active: !e.is_active } : e
      ));
      toast.success(`Card ${employee.is_active ? 'deactivated' : 'activated'}`);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleShowQR = async (employee) => {
    try {
      const response = await employeeAPI.getQR(employee.id);
      setQrDialog({ open: true, employee, qrData: response.data });
    } catch (error) {
      toast.error('Failed to generate QR code');
    }
  };

  const handleDownloadQR = async () => {
    if (!qrDialog.qrData) return;
    
    try {
      let qrUrl;
      
      // Check if we have SVG QR code from backend
      if (qrDialog.qrData.qr_code && qrDialog.qrData.qr_code_type === 'svg') {
        // Decode base64 SVG
        const svgData = atob(qrDialog.qrData.qr_code);
        const blob = new Blob([svgData], { type: 'image/svg+xml' });
        qrUrl = URL.createObjectURL(blob);
        
        // Convert SVG to PNG at 1200x1200
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = 1200;
          canvas.height = 1200;
          const ctx = canvas.getContext('2d');
          ctx.fillStyle = 'white';
          ctx.fillRect(0, 0, 1200, 1200);
          ctx.drawImage(img, 0, 0, 1200, 1200);
          
          canvas.toBlob((blob) => {
            const link = document.createElement('a');
            link.download = `${qrDialog.employee.full_name.replace(/\s+/g, '_')}_QR_1200x1200.png`;
            link.href = URL.createObjectURL(blob);
            link.click();
            URL.revokeObjectURL(link.href);
            URL.revokeObjectURL(qrUrl);
            toast.success('QR code downloaded (1200x1200 - 300 DPI)');
          }, 'image/png');
        };
        img.src = qrUrl;
      } else {
        // Fallback to external API
        qrUrl = qrDialog.qrData.qr_code_url || `https://api.qrserver.com/v1/create-qr-code/?size=1200x1200&data=${encodeURIComponent(qrDialog.qrData.card_url)}`;
        const link = document.createElement('a');
        link.download = `${qrDialog.employee.full_name.replace(/\s+/g, '_')}_QR.png`;
        link.href = qrUrl;
        link.target = '_blank';
        link.click();
        toast.success('Opening QR code - right-click to save');
      }
    } catch (error) {
      console.error('QR download error:', error);
      toast.error('Failed to download QR code');
    }
  };

  const filteredEmployees = employees.filter(emp => 
    emp.full_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    emp.job_title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    emp.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Employees</h1>
          <p className="text-slate-600 mt-1">{employees.length} total employee cards</p>
        </div>
        <Link to="/admin/employees/new">
          <Button className="btn-primary" data-testid="add-employee-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Employee
          </Button>
        </Link>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
        <Input
          placeholder="Search by name, title, or email..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-11"
          data-testid="search-input"
        />
      </div>

      {/* Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl border border-slate-100 overflow-hidden"
      >
        {filteredEmployees.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <Search className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="font-semibold text-slate-900 mb-1">
              {searchQuery ? 'No results found' : 'No employees yet'}
            </h3>
            <p className="text-slate-500 text-sm">
              {searchQuery 
                ? 'Try adjusting your search query' 
                : 'Add your first employee to get started'
              }
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Job Title</th>
                  <th>Contact</th>
                  <th>Scans</th>
                  <th>Status</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredEmployees.map((employee) => (
                  <tr key={employee.id} data-testid={`employee-row-${employee.id}`}>
                    <td>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl overflow-hidden bg-slate-100 flex-shrink-0">
                          {employee.profile_photo ? (
                            <img 
                              src={employee.profile_photo} 
                              alt={employee.full_name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#0F4C81] to-[#00A3C4] text-white font-semibold text-sm">
                              {employee.full_name.split(' ').map(n => n[0]).join('')}
                            </div>
                          )}
                        </div>
                        <span className="font-medium text-slate-900">{employee.full_name}</span>
                      </div>
                    </td>
                    <td className="text-slate-600">{employee.job_title}</td>
                    <td>
                      <div className="text-sm">
                        <p className="text-slate-900">{employee.email}</p>
                        <p className="text-slate-500">{employee.phone}</p>
                      </div>
                    </td>
                    <td>
                      <span className="font-mono text-sm text-slate-600">
                        {employee.scan_count}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge ${employee.is_active ? 'active' : 'inactive'}`}>
                        {employee.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            data-testid={`employee-actions-${employee.id}`}
                          >
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/admin/employees/${employee.id}/edit`)}>
                            <Edit2 className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleShowQR(employee)}>
                            <QrCode className="w-4 h-4 mr-2" />
                            View QR Code
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => window.open(`/card/${employee.slug}`, '_blank')}
                          >
                            <ExternalLink className="w-4 h-4 mr-2" />
                            Preview Card
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleToggleActive(employee)}>
                            {employee.is_active ? (
                              <>
                                <EyeOff className="w-4 h-4 mr-2" />
                                Deactivate
                              </>
                            ) : (
                              <>
                                <Eye className="w-4 h-4 mr-2" />
                                Activate
                              </>
                            )}
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            className="text-red-600 focus:text-red-600"
                            onClick={() => setDeleteDialog({ open: true, employee })}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </motion.div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Employee Card</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the card for {deleteDialog.employee?.full_name}? 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* QR Code Dialog */}
      <Dialog open={qrDialog.open} onOpenChange={(open) => setQrDialog({ ...qrDialog, open })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>QR Code - {qrDialog.employee?.full_name}</DialogTitle>
          </DialogHeader>
          {qrDialog.qrData && (
            <div className="flex flex-col items-center gap-6 py-4">
              <div className="qr-container bg-white p-6 rounded-xl border border-slate-200">
                <img 
                  src={qrDialog.qrData.qr_code && qrDialog.qrData.qr_code_type === 'svg' 
                    ? `data:image/svg+xml;base64,${qrDialog.qrData.qr_code}` 
                    : (qrDialog.qrData.qr_code_url || `https://api.qrserver.com/v1/create-qr-code/?size=1200x1200&data=${encodeURIComponent(qrDialog.qrData.card_url)}`)}
                  alt="QR Code"
                  className="w-48 h-48"
                  crossOrigin="anonymous"
                />
              </div>
              <p className="text-sm text-slate-500 text-center break-all">
                {qrDialog.qrData.card_url}
              </p>
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    navigator.clipboard.writeText(qrDialog.qrData.card_url);
                    toast.success('Link copied!');
                  }}
                >
                  Copy Link
                </Button>
                <Button onClick={handleDownloadQR} className="btn-primary">
                  <Download className="w-4 h-4 mr-2" />
                  Download QR
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmployeeList;